/* eslint-disable no-nested-ternary */
import classNames from 'classnames';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import React, { useContext, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { Box, Tooltip } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { isEmpty, isNil } from 'lodash';
import {
  Button,
  ConditionalWrapper,
  FieldCheckbox,
  FormattedMessage,
  IconInfo,
  IconShopifySync,
  IconSpinner,
  InlineTextButton,
  ListingImage,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  NamedLink,
  Overlay,
  TypographyWrapper,
} from '..';
import { logDiscardDraft } from '../../analytics/pixelAnalytics';
import {
  deleteDraftRequest,
  deleteDraftSuccess,
  deleteDraft as deleteMarketplaceDraft,
  deleteListing as deleteMarketplaceListing,
  updateListingQuantity,
} from '../../containers/ManageListingsPage/ManageListingsPage.duck';
import {
  deleteDraft as deleteBuybackDraft,
  deleteListing as deleteBuybackListing,
} from '../../containers/ManageTradeInsPage/ManageTradeInsPage.duck';
import { useShopConfig } from '../../hooks/shopConfig';
import {
  ITEM_AVAILABILITY_PURCHASED,
  LIST_ITEM_CARD_DEFAULT_OVERFLOW_WIDTH,
} from '../../util/constants';
import { formatMoney } from '../../util/currency';
import { ensureOwnListing } from '../../util/data';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import {
  LISTING_STATE_CLOSED,
  LISTING_STATE_DRAFT,
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_PUBLISHED,
  propTypes,
} from '../../util/types';
import { createSlug, ListingPageParamType } from '../../util/urlHelpers';
import { useElementSize } from '../../hooks/useElementSize';
import { useRouteConfiguration } from '../../hooks/useRouteConfiguration';
import { useCurrentUserPermissions } from '../../hooks/useUserPermissions';
import ConfirmDeleteListingModal from './ConfirmDeleteListingModal';
import MenuIcon from './MenuIcon';
import { createListingURLFromListing } from '../../util/listings/listing';
import css from './ManageListingCard.module.css';
import { TypographyFormat, TypographyWeight } from '../TypographyWrapper/TypographyWrapper';
import UpdateQuantityModal, { NEW_QUANTITY_FORM_FIELD_NAME } from './UpdateQuantityModal';
import { handle } from '../../util/helpers';
import { useListingFlowConfig } from '../../hooks/useListingFlowConfig';
import { SubmitTradeInsFormFields } from '../../containers/ManageTradeInsPage/ManageTradeInsPage.utils';
import { ListingItemType } from '../../types/sharetribe/listing';
import { useYotpo } from '../../hooks/useYotpo';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ModalType } from '../../ducks/modal.duck';
import { useActiveModal } from '../../hooks/useActiveModal';
import { useFeaturedListingImage } from '../../hooks/images';
import {
  buildPGDraftListingPayloadFromExistingListing,
  buildSTDraftListingPayloadFromExistingListing,
} from '../../util/listings/listingPayload';
import { buildDuplicateListingParams } from './ManageListingCard.utils';
import AppContext from '../../context/AppContext';
import { Feature, isFeatureEnabled } from '../../util/featureFlags';
import { createPGListingDraftFromExistingListing } from '../../containers/ManagePurchasesPage/ManagePurchasesPage.duck';
import { useDeleteDraftListing } from '../../types/apollo/generated/types.generated';
import { storableError } from '../../util/errors';
import CreditBoostChip from '../CreditBoostChip/CreditBoostChip';

// Menu content needs the same padding
const MENU_CONTENT_OFFSET = 9;
const MAX_LENGTH_FOR_WORDS_IN_TITLE = 7;

const priceData = (price, intl) => {
  if (price && !isNil(price.amount) && price.currency) {
    const formattedPrice = formatMoney(intl, price);
    return {
      formattedPrice,
      priceTitle: formattedPrice,
    };
  }
  return {};
};

const relistListingOnClick = async (currentListing, onRelistListing, slug, history, routes) => {
  const relistResponse = await onRelistListing(currentListing.id);
  if (relistResponse) {
    const to = createResourceLocatorString(
      'EditListingPage',
      routes,
      {
        slug,
        id: currentListing.id.uuid,
        type: ListingPageParamType.Edit,
        tab: 'details',
      },
      {}
    );
    history.push(to);
  }
};

const relistAsDuplicateOnClick = async (
  currentListing,
  onRelistAsDuplicateListing,
  slug,
  history,
  routes,
  internationalConfig,
  treetId,
  dispatch,
  shopId
) => {
  const params = buildDuplicateListingParams(currentListing);
  const isListingFlowV2 = isFeatureEnabled(Feature.ListingFlowV2, treetId);

  let duplicatedListingId;
  let listingType = ListingPageParamType.Edit;

  if (isListingFlowV2) {
    // V2: Create PostgreSQL draft listing
    const draftListingPayload = await buildPGDraftListingPayloadFromExistingListing(
      { ...params, internationalConfig },
      shopId
    );
    const createdListing = await dispatch(
      createPGListingDraftFromExistingListing(draftListingPayload)
    );
    duplicatedListingId = createdListing?.id;
    listingType = ListingPageParamType.Draft;
  } else {
    // V1: Create Sharetribe draft listing
    duplicatedListingId = await onRelistAsDuplicateListing(
      buildSTDraftListingPayloadFromExistingListing({ ...params, internationalConfig }, false),
      currentListing.id
    );
  }

  if (duplicatedListingId) {
    const to = createResourceLocatorString(
      'EditListingPage',
      routes,
      {
        slug,
        id: duplicatedListingId,
        type: listingType,
        tab: 'details',
      },
      {}
    );
    history.push(to);
  }
};

// Cards are not fixed sizes - So, long words in title make flexboxed items to grow too big.
// 1. We split title to an array of words and spaces.
//    "foo bar".split(/([^\s]+)/gi) => ["", "foo", " ", "bar", ""]
// 2. Then we break long words by adding a '<span>' with word-break: 'break-all';
const formatTitle = (title, maxLength) => {
  const nonWhiteSpaceSequence = /([^\s]+)/gi;
  return title?.split(nonWhiteSpaceSequence).map((word, index) =>
    word.length > maxLength ? (
      // eslint-disable-next-line react/no-array-index-key
      <span key={index} style={{ wordBreak: 'normal' }}>
        {word}
      </span>
    ) : (
      word
    )
  );
};

const MenuAction = (props) => {
  const { menuItemClasses, title, onClick } = props;
  return (
    <Box px={2}>
      <InlineTextButton
        rootClassName={menuItemClasses}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onClick();
        }}
      >
        <TypographyWrapper
          component="span"
          variant="body1"
          typographyOverrides={{ style: { fontSize: '11px' } }}
        >
          {title}
        </TypographyWrapper>
      </InlineTextButton>
    </Box>
  );
};

const MenuBar = (props) => {
  const {
    actionsInProgressListingId,
    currentListing,
    history,
    state,
    isMenuOpen,
    isPurchased,
    onCloseListing,
    onToggleMenu,
    slug,
    onDeleteListing,
    onOpenListing,
    onRelistListing,
    onRelistAsDuplicateListing,
    canRelist,
    canRelistAsDuplicate,
    pgListing,
  } = props;

  const dispatch = useDispatch();
  const routes = useRouteConfiguration();
  const { internationalConfig, shopName } = useShopConfig();
  const { isBrand } = useCurrentUserPermissions();
  const { treetId } = useContext(AppContext);
  const { shopId } = useSelector((pageState) => pageState.initial);

  const isDraft = state === LISTING_STATE_DRAFT;
  const isClosed = state === LISTING_STATE_CLOSED;
  const isPublished = state === LISTING_STATE_PUBLISHED;

  const isTradeIn =
    currentListing.attributes.publicData?.listingItemType === ListingItemType.TradeIn;

  const menuItemClasses = classNames(css.menuItem, {
    [css.menuItemDisabled]: !!actionsInProgressListingId,
  });
  const editListingLinkType = isDraft ? ListingPageParamType.Draft : ListingPageParamType.Edit;

  const editListingMenuAction = (
    <MenuItem key="edit-listing" className={css.menuItemWrapper}>
      <MenuAction
        menuItemClasses={menuItemClasses}
        title={isDraft ? 'Finish listing' : isTradeIn ? 'Edit item' : 'Edit listing'}
        onClick={() => {
          const to = createResourceLocatorString(
            'EditListingPage',
            routes,
            {
              slug,
              id: currentListing.id.uuid,
              type: editListingLinkType,
              tab: isDraft ? 'shipping' : 'pricing',
            },
            {}
          );
          history.push(to);
        }}
      />
    </MenuItem>
  );

  const closeListingMenuAction = (
    <MenuItem key="close-listing" className={css.menuItemWrapper}>
      <MenuAction
        menuItemClasses={menuItemClasses}
        title={<FormattedMessage id="ManageListingCard.closeListing" />}
        onClick={() => {
          if (!actionsInProgressListingId) {
            onToggleMenu(null);
            onCloseListing(currentListing.id);
          }
        }}
      />
    </MenuItem>
  );

  const openListingMenuAction = (
    <MenuItem key="open-listing" className={css.menuItemWrapper}>
      <MenuAction
        menuItemClasses={menuItemClasses}
        title={<FormattedMessage id="ManageListingCard.openListing" />}
        onClick={() => {
          if (!actionsInProgressListingId) {
            onToggleMenu(null);
            onOpenListing(currentListing.id);
          }
        }}
      />
    </MenuItem>
  );

  const deleteListingMenuAction = (
    <MenuItem key="delete-listing" className={css.menuItemWrapper}>
      <MenuAction
        menuItemClasses={menuItemClasses}
        title={isDraft ? 'Delete draft' : isTradeIn ? 'Delete item' : 'Delete listing'}
        onClick={onDeleteListing}
      />
    </MenuItem>
  );

  const relistMenuAction = (
    <MenuItem key="relist-item" className={css.menuItemWrapper}>
      <MenuAction
        menuItemClasses={menuItemClasses}
        title="Relist listing"
        onClick={() => {
          if (!actionsInProgressListingId) {
            onToggleMenu(null);
            relistListingOnClick(currentListing, onRelistListing, slug, history, routes);
          }
        }}
      />
    </MenuItem>
  );

  const relistAsDuplicateMenuAction = (
    <MenuItem key="relist-item-as-duplicate" className={css.menuItemWrapper}>
      <MenuAction
        menuItemClasses={menuItemClasses}
        title="Relist listing"
        onClick={() => {
          if (!actionsInProgressListingId) {
            onToggleMenu(null);
            relistAsDuplicateOnClick(
              currentListing,
              onRelistAsDuplicateListing,
              slug,
              history,
              routes,
              internationalConfig,
              treetId,
              dispatch,
              shopId
            );
          }
        }}
      />
    </MenuItem>
  );

  const printLabelMenuAction = pgListing?.id ? (
    <MenuItem key="print-label" className={css.menuItemWrapper}>
      <MenuAction
        menuItemClasses={menuItemClasses}
        title="Print Label"
        onClick={() => {
          const labelWindow = window.open('', '_blank');

          const listingId = pgListing.id;
          // Choosing length of 12 because that's the typical length of a UPC code
          const truncatedListingId = listingId.slice(-12);

          // Returns two text lines in this format:
          // Line 1: Barcode: Truncated Listing ID; SKU: SKU123
          // Line 2: Shop Name Listing Title (Variant Options)
          // Line 3: Listing ID
          const constructTextLines = () => {
            const { title, publicData } = currentListing.attributes;
            const { selectedVariantOptions, shopifyProductVariantSku } = publicData || {};

            const variantText = selectedVariantOptions
              ? `(${Object.values(selectedVariantOptions).join(', ')})`
              : '';

            const skuText = shopifyProductVariantSku ? `; SKU: ${shopifyProductVariantSku}` : '';

            const textLine1 = `Barcode: ${truncatedListingId} ${skuText}`;
            const textLine2 = `${shopName} ${title} ${variantText}`.slice(0, 60);
            const textLine3 = `Listing ID: ${listingId}`;

            return { textLine1, textLine2, textLine3 };
          };

          // Helper function to escape HTML
          const escapeHtml = (text) => {
            const div = document.createElement('div');
            div.textContent = text;
            return div.innerHTML;
          };

          const { textLine1, textLine2, textLine3 } = constructTextLines();

          const htmlContent = `
              <html>
                <head>
                  <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.12.1/dist/JsBarcode.all.min.js"></script>
                </head>
                <body>
                  <div class="barcode-container">
                    <svg id="barcode"></svg>
                    <div class="barcode-text">${escapeHtml(textLine1)}</div>
                    <div class="barcode-text">${escapeHtml(textLine2)}</div>
                    <div class="barcode-text">${escapeHtml(textLine3)}</div>

                  </div>
                  <script>
                    document.addEventListener("DOMContentLoaded", function() {
                      JsBarcode("#barcode", "${truncatedListingId}", {
                        format: "code128",
                        displayValue: false,
                        height: 50,
                        width: 1.5,
                        margin: 0
                      });
                      // Once rendered, trigger print
                      requestAnimationFrame(() => window.print());
                    });
                  </script>
                  <style>
                    .barcode-container {
                      margin-left: 15px;
                    }
                    .barcode-text {
                      font-size: 10px;
                    }
                  </style>
                </body>
              </html>
            `;

          labelWindow.document.write(htmlContent);
          labelWindow.document.close();
        }}
      />
    </MenuItem>
  ) : null;

  const componentRef = useRef(null);
  const { width } = useElementSize(componentRef);

  const manageListingCardId = 'manage-listing-card-menu';

  const menuActions = [
    // Allow user to edit available (not purchased) listings
    !isPurchased && editListingMenuAction,
    // Allow user to close published listings
    isPublished && !isTradeIn && closeListingMenuAction,
    // Allow user to delete (not purchased) listings
    !isPurchased && deleteListingMenuAction,
    // Allow user to re-open closed listings that are still available
    isClosed && !isPurchased && openListingMenuAction,
    // Allow user to re-list closed and purchased listings
    canRelist && relistMenuAction,
    // Allow user to re-list purchased listings via duplication (BD only)
    !canRelist && canRelistAsDuplicate && relistAsDuplicateMenuAction,
    // Allow brands to print labels
    isBrand && printLabelMenuAction,
  ].filter((action) => action);

  return (
    <div className={css.menubarWrapper} ref={componentRef}>
      <div className={css.menubarGradient} />
      <div className={css.menubar}>
        {!isEmpty(menuActions) && (
          <Menu
            className={classNames(css.menu, css.cardIsOpen)}
            contentPlacementOffset={MENU_CONTENT_OFFSET}
            contentPosition="left"
            useArrow={false}
            onToggleActive={(isOpen) => {
              const listingOpen = isOpen ? currentListing : null;
              onToggleMenu(listingOpen);
            }}
            isOpen={isMenuOpen}
            mobileMenuContentStyleOverrides={{
              left: 'unset',
              right: 0,
              width: `${width}px`,
            }}
          >
            <MenuLabel
              className={css.menuLabel}
              isOpenClassName={css.listingMenuIsOpen}
              ariaLabel="Listing Actions Menu"
              contentId={manageListingCardId}
            >
              <div className={css.iconWrapper}>
                <MenuIcon className={css.menuIcon} isActive={isMenuOpen} />
              </div>
            </MenuLabel>
            <MenuContent rootClassName={css.menuContent} id={manageListingCardId}>
              {menuActions}
            </MenuContent>
          </Menu>
        )}
      </div>
    </div>
  );
};

const ListingCard = (props) => {
  const {
    actionsInProgressListingId,
    currentListing,
    hasClosingError,
    hasOpeningError,
    hasRelistError,
    history,
    intl,
    isMenuOpen,
    isPurchased,
    listing,
    onCloseListing,
    onOpenListing,
    onRelistListing,
    onRelistAsDuplicateListing,
    onToggleMenu,
    renderSizes,
    onDeleteListing,
    onSelectTradeInListing,
    isTradeInListingSelectable,
    hideMenu = false,
    canRelist,
    wasCanceled,
    pgListing,
  } = props;

  const dispatch = useDispatch();
  const { imageRatio, internationalConfig } = useShopConfig();
  const routes = useRouteConfiguration();
  const isMobile = useIsMobile();
  const componentRef = useRef(null);
  const maxWidthForOverflow = LIST_ITEM_CARD_DEFAULT_OVERFLOW_WIDTH;
  const { width } = useElementSize(componentRef);
  const featuredListingImage = useFeaturedListingImage(currentListing);
  const { treetId } = useContext(AppContext);
  const { shopId } = useSelector((state) => state.initial);

  const { title = '', state, publicData } = currentListing.attributes;
  const { isBrandDirect = false, listingItemType, syncShopifyInventory = false } = publicData || {};

  const isClosed = state === LISTING_STATE_CLOSED;
  const isPublished = state === LISTING_STATE_PUBLISHED;
  const isPendingApproval = state === LISTING_STATE_PENDING_APPROVAL;
  const isDraft = state === LISTING_STATE_DRAFT;

  const canRelistAsDuplicate = isBrandDirect && !isPublished;
  const isTradeIn = listingItemType === ListingItemType.TradeIn;

  const slug = createSlug(title);
  const hasFeaturedListingImage = !!featuredListingImage?.src;
  const thisListingInProgress =
    actionsInProgressListingId && actionsInProgressListingId.uuid === listing.id.uuid;
  const hasError = hasOpeningError || hasClosingError || hasRelistError;

  const listingImageClassName = classNames(css.threeToTwoWrapper, {
    [css.threeToTwoWrapperHover]: !isTradeIn || isTradeInListingSelectable,
  });

  const menuBar = hideMenu ? null : (
    <MenuBar
      actionsInProgressListingId={actionsInProgressListingId}
      currentListing={currentListing}
      history={history}
      state={state}
      isMenuOpen={isMenuOpen}
      isPurchased={isPurchased}
      onCloseListing={onCloseListing}
      onToggleMenu={onToggleMenu}
      slug={slug}
      canRelist={canRelist}
      canRelistAsDuplicate={canRelistAsDuplicate}
      onDeleteListing={onDeleteListing}
      onOpenListing={onOpenListing}
      onRelistListing={onRelistListing}
      onRelistAsDuplicateListing={onRelistAsDuplicateListing}
      pgListing={pgListing}
    />
  );

  const syncShopifyInventoryTooltip = (
    <Tooltip
      enterTouchDelay={0}
      title={
        <TypographyWrapper variant="body2" typographyOverrides={{ style: { color: 'inherit' } }}>
          This listing is cross-posted with Shopify
        </TypographyWrapper>
      }
      placement="right"
    >
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        className={css.syncShopifyInventoryTooltip}
      >
        <IconShopifySync />
      </Box>
    </Tooltip>
  );

  const onOverListingLink = () => {
    // Enforce preloading of ListingPage (loadable component)
    const { component: Page } = findRouteByRouteName('ListingPage', routes);
    // Loadable Component has a "preload" function.
    if (Page.preload) {
      Page.preload();
    }
  };

  const isOverflowWidth = isMobile || (!!width && width <= maxWidthForOverflow);
  const fontSize = isOverflowWidth ? '13px' : undefined;

  return (
    <ConditionalWrapper
      condition={!isTradeIn || isTradeInListingSelectable}
      wrapper={(children) => (
        <div
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();

            // ManageListingCard contains links, buttons and elements that are working with routing.
            // This card doesn't work if <a> or <button> is used to wrap events that are card 'clicks'.
            //
            // NOTE: It might be better to absolute-position those buttons over a card-links.
            // (So, that they have no parent-child relationship - like '<a>bla<a>blaa</a></a>')
            if (!isTradeIn) {
              history.push(createListingURLFromListing(routes, listing));
            } else if (isTradeInListingSelectable && onSelectTradeInListing) {
              onSelectTradeInListing(listing.id.uuid);
            }
          }}
          onMouseOver={onOverListingLink}
          onTouchStart={onOverListingLink}
          onFocus={onOverListingLink}
        >
          {children}
        </div>
      )}
    >
      <div className={listingImageClassName}>
        <div className={css.aspectWrapper} style={{ paddingBottom: `${imageRatio * 100}%` }}>
          <ListingImage
            currentListing={currentListing}
            rootClassName={css.rootForImage}
            title={title}
            variants={['default']}
            sizes={renderSizes}
          />
        </div>
        <div className={classNames(css.menuOverlayWrapper, { [css.menuOverlayOpen]: isMenuOpen })}>
          <div className={classNames(css.menuOverlay)} />
          <div className={css.menuOverlayContent}>
            <FormattedMessage id="ManageListingCard.viewListing" />
          </div>
        </div>
        {/* If the listing is published, show menu bar. Otherwise, for other listing states,
        the menu bar will live as a child inside the Overlay component so that it remains clickable
        and not swallowed by the full overlay. */}
        {isPublished && menuBar}
        {isPublished && syncShopifyInventory && syncShopifyInventoryTooltip}
        {isDraft && (
          <>
            <div className={classNames({ [css.draftNoImage]: !hasFeaturedListingImage })} />
            <Overlay
              message={intl.formatMessage({ id: 'ManageListingCard.draftOverlayText' })}
              shouldAllowClick
            >
              {menuBar}
              <Box display="flex" justifyContent="center" width="100%" mt={1}>
                <Button
                  className={css.listingActionButton}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    history.push(createListingURLFromListing(routes, listing));
                  }}
                  type="button"
                >
                  {isMobile ? 'Finish' : 'Finish Listing'}
                </Button>
              </Box>
            </Overlay>
          </>
        )}
        {isClosed && isPurchased && (
          <Overlay
            message={wasCanceled ? 'CANCELED' : isTradeIn ? 'SUBMITTED' : 'SOLD'}
            shouldAllowClick={!isTradeIn}
          >
            {!(isTradeIn && !wasCanceled) && menuBar}
            {canRelist && (
              <Box display="flex" justifyContent="center" width="100%" mt={1}>
                <Button
                  className={css.listingActionButton}
                  disabled={!!actionsInProgressListingId}
                  title="Reopen existing listing"
                  onClick={(event) => {
                    if (!actionsInProgressListingId) {
                      event.stopPropagation();
                      relistListingOnClick(currentListing, onRelistListing, slug, history, routes);
                    }
                  }}
                  type="button"
                >
                  {isMobile ? 'Relist' : 'Relist Listing'}
                </Button>
              </Box>
            )}
            {/* If it is not a return or a cancelation, and the listing is brand direct, show the relist via duplicate button */}
            {!canRelist && canRelistAsDuplicate && (
              <Box display="flex" justifyContent="center" width="100%" mt={1}>
                <Button
                  className={css.listingActionButton}
                  disabled={!!actionsInProgressListingId}
                  title="Relist listing by creating duplicate"
                  onClick={(event) => {
                    if (!actionsInProgressListingId) {
                      event.stopPropagation();
                      relistAsDuplicateOnClick(
                        currentListing,
                        onRelistAsDuplicateListing,
                        slug,
                        history,
                        routes,
                        internationalConfig,
                        treetId,
                        dispatch,
                        shopId
                      );
                    }
                  }}
                  type="button"
                >
                  {isMobile ? 'Relist' : 'Relist Listing'}
                </Button>
              </Box>
            )}
          </Overlay>
        )}
        {isClosed && !isPurchased && (
          <Overlay
            message={
              isTradeIn
                ? 'This listing is closed.'
                : isMobile
                ? 'This listing is closed and not purchasable.'
                : intl.formatMessage({ id: 'ManageListingCard.closedListing' })
            }
            shouldAllowClick
            fontSize={fontSize}
          >
            {menuBar}
            <Box display="flex" justifyContent="center" width="100%" mt={1}>
              <Button
                className={css.listingActionButton}
                disabled={!!actionsInProgressListingId}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (!actionsInProgressListingId) {
                    onOpenListing(currentListing.id);
                  }
                }}
                type="button"
              >
                {isMobile ? 'Open' : 'Open Listing'}
              </Button>
            </Box>
          </Overlay>
        )}
        {isPendingApproval && (
          <Overlay
            message="This listing is pending admin approval."
            shouldAllowClick={!isTradeIn}
            fontSize={fontSize}
          >
            {menuBar}
            <Box display="flex" justifyContent="center" width="100%" mt={1}>
              <Button
                className={css.listingActionButton}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  history.push(createListingURLFromListing(routes, listing));
                }}
                type="button"
              >
                {isMobile ? 'View' : 'View Listing'}
              </Button>
            </Box>
          </Overlay>
        )}
        {thisListingInProgress ? (
          <Overlay>
            <IconSpinner />
          </Overlay>
        ) : hasError ? (
          <Overlay errorMessage={intl.formatMessage({ id: 'ManageListingCard.actionFailed' })} />
        ) : null}
      </div>
    </ConditionalWrapper>
  );
};

const TradeInListingInfo = (props) => {
  const { currentListing, isPurchased, isTradeInListingSelectable, tradeInPriceHelperText } = props;

  const intl = useIntl();
  const { doesBrandUseLoyaltyPoints, getPointsFromPrice } = useYotpo();

  const { price } = currentListing.attributes;
  const { formattedPrice, priceTitle } = priceData(price, intl);

  const formattedYotpoPoints = doesBrandUseLoyaltyPoints && getPointsFromPrice(price?.amount);

  const titleClasses = classNames(css.title);

  const showTradeInPrice = Number(price?.amount) !== 0;

  const amountReceiveText = tradeInPriceHelperText || `You’ll receive`;

  return (
    <div className={css.info}>
      <div className={css.mainInfo}>
        <div className={css.titleWrapper}>
          <TypographyWrapper
            rootClassName={titleClasses}
            variant="body1"
            weight={TypographyWeight.Bold}
            format={TypographyFormat.HoverUnderlined}
            isDynamicValue
          >
            {formatTitle(currentListing?.attributes?.title, MAX_LENGTH_FOR_WORDS_IN_TITLE)}
          </TypographyWrapper>
        </div>
      </div>
      {isTradeInListingSelectable && !isPurchased && formattedPrice && showTradeInPrice && (
        <div className={css.priceValue} title={priceTitle}>
          <TypographyWrapper variant="body2" typographyOverrides={{ display: 'inline' }}>
            {amountReceiveText}
          </TypographyWrapper>{' '}
          <TypographyWrapper
            variant="body2"
            weight={TypographyWeight.Bold}
            typographyOverrides={{ display: 'inline' }}
          >
            {doesBrandUseLoyaltyPoints ? (
              `${formattedYotpoPoints} points`
            ) : (
              <TypographyWrapper
                variant="body2"
                weight={TypographyWeight.Bold}
                typographyOverrides={{ display: 'inline' }}
                isDynamicValue
              >
                {formattedPrice}
              </TypographyWrapper>
            )}
          </TypographyWrapper>
        </div>
      )}
    </div>
  );
};

const InfoLine = (props) => {
  const { title, value } = props;

  return (
    <Box display="flex" alignItems="center" marginY="3px">
      <TypographyWrapper
        variant="body2"
        weight="bold"
        typographyOverrides={{
          style: { marginRight: '3px' },
        }}
      >
        {title}:
      </TypographyWrapper>
      <TypographyWrapper variant="body2" isDynamicValue>
        {value}
      </TypographyWrapper>
    </Box>
  );
};

const ListingInfo = (props) => {
  const {
    currentListing,
    history,
    intl,
    isPurchased: isPurchasedListing,
    listing,
    lastPurchaseBundleId,
    isTradeInListingSelectable,
    onUpdateQuantity,
    currentQuantity,
  } = props;

  const { sizeVariantOptionName } = useShopConfig();
  const { tradeInPriceHelperText } = useListingFlowConfig();
  const routes = useRouteConfiguration();
  const { isBrand } = useCurrentUserPermissions();

  const {
    price,
    publicData: {
      isBrandDirect,
      color,
      shopifyProductVariantSku,
      listingItemType,
      syncShopifyInventory,
    },
    title,
    state,
  } = currentListing.attributes;
  const isPublished = state === LISTING_STATE_PUBLISHED;
  const isDraft = state === LISTING_STATE_DRAFT;
  const size = currentListing.attributes.publicData?.[sizeVariantOptionName];
  const isTradeIn = listingItemType === ListingItemType.TradeIn;

  const titleClasses = classNames(css.title);

  const { formattedPrice, priceTitle } = priceData(price, intl);

  if (isTradeIn) {
    return (
      <TradeInListingInfo
        currentListing={currentListing}
        isPurchased={isPurchasedListing}
        isTradeInListingSelectable={isTradeInListingSelectable}
        tradeInPriceHelperText={tradeInPriceHelperText}
      />
    );
  }

  return (
    <div className={css.info}>
      <div className={css.mainInfo}>
        <div className={css.titleWrapper}>
          <InlineTextButton
            rootClassName={titleClasses}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              history.push(createListingURLFromListing(routes, listing));
            }}
          >
            <TypographyWrapper
              variant="body1"
              component="span"
              typographyOverrides={{ style: { fontWeight: 'bold' } }}
              format={TypographyFormat.HoverUnderlined}
              isDynamicValue
            >
              {formatTitle(title, MAX_LENGTH_FOR_WORDS_IN_TITLE)}
            </TypographyWrapper>
          </InlineTextButton>
        </div>
      </div>

      <div className={css.price}>
        <div className={css.priceValue} title={priceTitle}>
          <TypographyWrapper variant="body2">
            {formattedPrice ? (
              <TypographyWrapper
                variant="body2"
                weight={TypographyWeight.Bold}
                typographyOverrides={{ display: 'inline' }}
                isDynamicValue
              >
                {formattedPrice}
              </TypographyWrapper>
            ) : (
              <FormattedMessage id="ManageListingCard.priceNotSet" />
            )}
          </TypographyWrapper>
        </div>
      </div>
      {!!isBrandDirect && (
        <>
          {size && <InfoLine title="Size" value={size} />}
          {color && <InfoLine title="Color" value={color} />}
          {isBrand && shopifyProductVariantSku && (
            <InfoLine title="SKU" value={shopifyProductVariantSku} />
          )}
          {isBrand && (
            <Box display="flex" flexDirection="row" alignItems="center">
              <InfoLine title="Quantity" value={currentQuantity} />
              {isPublished && !syncShopifyInventory && (
                <InlineTextButton onClick={() => onUpdateQuantity()}>
                  <TypographyWrapper
                    variant="body2"
                    format={TypographyFormat.Underlined}
                    typographyOverrides={{ style: { marginLeft: '8px' } }}
                  >
                    Edit
                  </TypographyWrapper>
                </InlineTextButton>
              )}
              {isPublished && syncShopifyInventory && (
                <Box mx={1}>
                  <Tooltip
                    enterTouchDelay={0}
                    title={
                      <TypographyWrapper
                        variant="body2"
                        typographyOverrides={{ style: { color: 'inherit' } }}
                      >
                        Manage inventory for this listing in your Shopify Admin.
                      </TypographyWrapper>
                    }
                    placement="right"
                  >
                    <Box display="flex" alignItems="center">
                      <IconInfo height="12" width="12" />
                    </Box>
                  </Tooltip>
                </Box>
              )}
            </Box>
          )}
        </>
      )}
      {isPurchasedListing && lastPurchaseBundleId && (
        <NamedLink name="ManageSalePage" params={{ id: lastPurchaseBundleId }}>
          <TypographyWrapper
            variant="body1"
            format={TypographyFormat.Underlined}
            typographyOverrides={{ textAlign: 'left' }}
          >
            Manage Sale
          </TypographyWrapper>
        </NamedLink>
      )}
      {!isDraft && <CreditBoostChip listing={currentListing} />}
    </div>
  );
};

export const ManageListingCardComponent = (props) => {
  const {
    className,
    rootClassName,
    hasClosingError,
    hasOpeningError,
    hasRelistError,
    history,
    intl,
    isMenuOpen,
    actionsInProgressListingId,
    listing,
    onCloseListing,
    onOpenListing,
    onRelistListing,
    onRelistAsDuplicateListing,
    onToggleMenu,
    onSelectTradeInListing,
    renderSizes,
    currentUser,
    hideMenu,
    pgListing,
  } = props;

  const dispatch = useDispatch();
  const {
    isModalOpen: isConfirmDeleteModalOpen,
    openModal: openConfirmDeleteModal,
    closeModal: closeConfirmDeleteModal,
  } = useActiveModal(ModalType.ConfirmDelete);
  const {
    isModalOpen: isUpdateQuantityModalOpen,
    openModal: openUpdateQuantityModal,
    closeModal: closeUpdateQuantityModal,
  } = useActiveModal(ModalType.UpdateQuantity);

  const [updateQuantityModalOpen, setUpdateQuantityModalOpen] = useState(false);
  const [confirmDeleteListingModalOpen, setConfirmDeleteListingModalOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { shopName } = useShopConfig();
  const { treetId } = useContext(AppContext);
  const [deleteDraftListing] = useDeleteDraftListing();

  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureOwnListing(listing);
  const id = currentListing.id.uuid;
  const {
    title = '',
    state,
    publicData: { availability, listingItemType },
  } = currentListing.attributes;
  const slug = createSlug(title);

  const canRelist = pgListing?.canRelist ?? false;
  const lastPurchaseBundleId = pgListing?.lastPurchaseBundleId ?? null;
  const wasCanceled = pgListing?.wasCanceled ?? false;

  // The bundle prop is the most recent bundle associated with the listing.
  // If there is a bundle associated with the listing, this means it was purchased and should not be deleted.
  // We don't want these listings to be deleted because this affects payout jobs.
  const isPurchased = availability === ITEM_AVAILABILITY_PURCHASED || !!lastPurchaseBundleId;
  const isDraft = state === LISTING_STATE_DRAFT;
  const isPublished = state === LISTING_STATE_PUBLISHED;
  const isTradeIn = listingItemType === ListingItemType.TradeIn;

  const isCurrentShopListing = listing?.attributes?.publicData?.shopName === shopName;
  const isTradeInListingSelectable = isTradeIn && isPublished && isCurrentShopListing;

  const quantity = listing.currentStock?.attributes.quantity;
  const currentQuantity = quantity == null ? 1 : quantity;

  const handleDelete = async () => {
    if (!isDraft) {
      const deleteListing = isTradeIn ? deleteBuybackListing : deleteMarketplaceListing;

      const [, deleteListingError] = await handle(dispatch(deleteListing(currentListing.id)));

      if (deleteListingError) {
        enqueueSnackbar('Something went wrong. Please contact support@treet.co.', {
          variant: 'error',
          transitionDuration: { enter: 800, exit: 500 },
        });
      } else {
        enqueueSnackbar('Listing successfully deleted.', {
          variant: 'success',
          transitionDuration: { enter: 800, exit: 500 },
        });
      }
    } else {
      let deleteDraftError;
      if (isFeatureEnabled(Feature.ListingFlowV2, treetId)) {
        const listingId = currentListing.id;
        dispatch(deleteDraftRequest(listingId));
        try {
          await deleteDraftListing({
            variables: {
              input: {
                id: listingId.uuid,
              },
            },
          });
          dispatch(deleteDraftSuccess(listingId));
        } catch (error) {
          storableError(error);
          deleteDraftError = error;
        }
      } else {
        const deleteDraft = isTradeIn ? deleteBuybackDraft : deleteMarketplaceDraft;

        [, deleteDraftError] = await handle(dispatch(deleteDraft(currentListing.id)));
      }

      logDiscardDraft();
      enqueueSnackbar('Listing successfully deleted.', {
        variant: 'success',
        transitionDuration: { enter: 800, exit: 500 },
      });
      if (deleteDraftError) {
        enqueueSnackbar('Something went wrong. Please contact support@treet.co.', {
          variant: 'error',
          transitionDuration: { enter: 800, exit: 500 },
        });
      }
    }
  };

  const handleUpdateQuantity = async (listingId, oldQuantity, newQuantity) => {
    const [updateListingResponse, updateListingError] = await handle(
      dispatch(updateListingQuantity(listingId, oldQuantity, newQuantity))
    );

    if (!updateListingResponse || updateListingError) {
      enqueueSnackbar('Issue updating listing quantity. Please refresh and try again.', {
        variant: 'error',
        transitionDuration: { enter: 800, exit: 500 },
      });
      console.error(updateListingError);
    } else {
      enqueueSnackbar('Successfully updated quantity', {
        variant: 'success',
        transitionDuration: { enter: 800, exit: 500 },
      });
    }

    setUpdateQuantityModalOpen(false);
    closeUpdateQuantityModal();
  };

  const listingCard = isDraft ? (
    <NamedLink
      className={css.finishListingDraftLink}
      name="EditListingPage"
      params={{ id, slug, type: ListingPageParamType.Draft, tab: 'shipping' }}
    >
      <ListingCard
        actionsInProgressListingId={actionsInProgressListingId}
        currentListing={currentListing}
        hasClosingError={hasClosingError}
        hasOpeningError={hasOpeningError}
        hasRelistError={hasRelistError}
        history={history}
        intl={intl}
        isMenuOpen={isMenuOpen}
        isPurchased={isPurchased}
        listing={listing}
        onCloseListing={onCloseListing}
        onOpenListing={onOpenListing}
        onRelistListing={onRelistListing}
        onRelistAsDuplicateListing={onRelistAsDuplicateListing}
        onToggleMenu={onToggleMenu}
        onSelectTradeInListing={onSelectTradeInListing}
        renderSizes={renderSizes}
        onDeleteListing={() => {
          setConfirmDeleteListingModalOpen(true);
          openConfirmDeleteModal();
        }}
        canRelist={canRelist}
        lastPurchaseBundleId={lastPurchaseBundleId}
        wasCanceled={wasCanceled}
        hideMenu={hideMenu}
        pgListing={pgListing}
      />
    </NamedLink>
  ) : (
    <ListingCard
      actionsInProgressListingId={actionsInProgressListingId}
      currentListing={currentListing}
      hasClosingError={hasClosingError}
      hasOpeningError={hasOpeningError}
      hasRelistError={hasRelistError}
      history={history}
      intl={intl}
      isMenuOpen={isMenuOpen}
      isPurchased={isPurchased}
      listing={listing}
      onCloseListing={onCloseListing}
      onOpenListing={onOpenListing}
      onRelistListing={onRelistListing}
      onRelistAsDuplicateListing={onRelistAsDuplicateListing}
      onToggleMenu={onToggleMenu}
      onSelectTradeInListing={onSelectTradeInListing}
      renderSizes={renderSizes}
      isTradeInListingSelectable={isTradeInListingSelectable}
      onDeleteListing={() => {
        setConfirmDeleteListingModalOpen(true);
        openConfirmDeleteModal();
      }}
      canRelist={canRelist}
      lastPurchaseBundleId={lastPurchaseBundleId}
      wasCanceled={wasCanceled}
      hideMenu={hideMenu}
      pgListing={pgListing}
    />
  );

  return (
    <div className={classes}>
      {listingCard}
      <Box display="flex" justifyContent="space-between">
        <ListingInfo
          currentListing={currentListing}
          currentUser={currentUser}
          history={history}
          intl={intl}
          isPurchased={isPurchased}
          listing={listing}
          isTradeInListingSelectable={isTradeInListingSelectable}
          onUpdateQuantity={() => {
            setUpdateQuantityModalOpen(true);
            openUpdateQuantityModal();
          }}
          currentQuantity={currentQuantity}
          lastPurchaseBundleId={lastPurchaseBundleId}
        />
        {isTradeInListingSelectable && (
          <Box ml={1}>
            <FieldCheckbox
              id={`listings.${currentListing.id.uuid}`}
              name={SubmitTradeInsFormFields.ListingIds}
              value={currentListing.id.uuid}
              wrapperClassName={css.manageTradeInCheckbox}
              svgClassName={css.boxClassName}
            />
          </Box>
        )}
      </Box>
      {confirmDeleteListingModalOpen && (
        <ConfirmDeleteListingModal
          open={isConfirmDeleteModalOpen}
          onClose={() => {
            setConfirmDeleteListingModalOpen(false);
            closeConfirmDeleteModal();
          }}
          inProgress={actionsInProgressListingId}
          onSubmit={() => {
            if (!actionsInProgressListingId) {
              handleDelete();
            }
          }}
        />
      )}
      {updateQuantityModalOpen && (
        <UpdateQuantityModal
          open={isUpdateQuantityModalOpen}
          onClose={() => {
            setUpdateQuantityModalOpen(false);
            closeUpdateQuantityModal();
          }}
          inProgress={actionsInProgressListingId}
          onSubmit={(values) => {
            if (!actionsInProgressListingId) {
              handleUpdateQuantity(
                listing.id.uuid,
                currentQuantity,
                values[NEW_QUANTITY_FORM_FIELD_NAME]
              );
            }
          }}
          listingTitle={listing.attributes.title}
          currentQuantity={currentQuantity}
        />
      )}
    </div>
  );
};

ManageListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  actionsInProgressListingId: null,
  renderSizes: null,
  onSelectTradeInListing: null,
  hideMenu: false,
  pgListing: null,
};

const { bool, func, shape, string } = PropTypes;

ManageListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  hasClosingError: bool.isRequired,
  hasOpeningError: bool.isRequired,
  hasRelistError: bool.isRequired,
  intl: intlShape.isRequired,
  listing: propTypes.ownListing.isRequired,
  isMenuOpen: bool.isRequired,
  actionsInProgressListingId: shape({ uuid: string.isRequired }),
  onCloseListing: func.isRequired,
  onOpenListing: func.isRequired,
  onRelistListing: func.isRequired,
  onRelistAsDuplicateListing: func.isRequired,
  onToggleMenu: func.isRequired,
  onSelectTradeInListing: func,
  // Responsive image sizes hint
  renderSizes: string,
  hideMenu: bool,
  // PostgreSQL listing data from listingsToManage
  pgListing: shape({
    id: string,
    sharetribeListingId: string,
    canRelist: bool,
    lastPurchaseBundleId: string,
    wasCanceled: bool,
  }),

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
};

export default compose(withRouter, injectIntl)(ManageListingCardComponent);
